
import React from "react";
import { Users, X } from "lucide-react";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ProfileFilters } from "@/hooks/useProfileFilters";
import { formatActiveFilters } from "@/utils/filterLabels";

interface EmptyProfilesProps {
  customMessage?: string;
  hasActiveFilters?: boolean;
  onClearFilters?: () => void;
  activeFilters?: ProfileFilters;
}

const EmptyProfiles: React.FC<EmptyProfilesProps> = ({ 
  customMessage, 
  hasActiveFilters = false, 
  onClearFilters,
  activeFilters
}) => {
  const isOnboardingMessage = customMessage?.includes("onboarding");
  const formattedFilters = activeFilters ? formatActiveFilters(activeFilters) : [];
  
  return (
    <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
      <div className="bg-black/40 p-6 rounded-xl border border-white/10 flex flex-col items-center max-w-md w-full">
        <div className="w-16 h-16 bg-white/5 rounded-full flex items-center justify-center mb-4">
          <Users className="h-8 w-8 text-luxury-red" />
        </div>
        <h2 className="text-xl font-bold text-white mb-2">
          {hasActiveFilters ? "Nenhum resultado encontrado" : "Nenhum perfil encontrado"}
        </h2>
        <p className="text-gray-400 mb-4">
          {hasActiveFilters 
            ? "Não encontramos perfis que correspondam aos seus filtros."
            : (customMessage || "Não encontramos perfis para exibir no momento. Tente novamente mais tarde ou ajuste seus critérios de busca.")
          }
        </p>
        
        {hasActiveFilters && formattedFilters.length > 0 && (
          <div className="w-full mb-4">
            <div className="bg-white/5 rounded-lg p-4 border border-white/10">
              <p className="text-sm font-medium text-white mb-3">Filtros ativos:</p>
              <div className="space-y-2">
                {formattedFilters.map((filter, index) => (
                  <div key={index} className="flex items-start gap-2 text-left">
                    <X className="h-4 w-4 text-red-400 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                      <p className="text-xs font-medium text-gray-300">{filter.label}</p>
                      <p className="text-xs text-gray-400">{filter.values.join(', ')}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}

        {hasActiveFilters && formattedFilters.length > 0 && (
          <div className="w-full mb-4">
            <div className="bg-luxury-purple/10 rounded-lg p-3 border border-luxury-purple/20">
              <p className="text-xs font-medium text-luxury-purple mb-2">💡 Sugestões:</p>
              <ul className="text-xs text-gray-400 space-y-1 text-left list-disc list-inside">
                {activeFilters?.minAge !== 18 || activeFilters?.maxAge !== 65 ? (
                  <li>Expandir a faixa etária</li>
                ) : null}
                {formattedFilters.length > 2 && (
                  <li>Remover alguns filtros para ver mais perfis</li>
                )}
                <li>Limpar todos os filtros abaixo</li>
              </ul>
            </div>
          </div>
        )}
        
        {hasActiveFilters && onClearFilters && (
          <Button 
            onClick={onClearFilters}
            className="bg-luxury-red hover:bg-luxury-red/90 mb-4 w-full"
          >
            Limpar Todos os Filtros
          </Button>
        )}
        
        {isOnboardingMessage && (
          <Link to="/main/onboarding">
            <Button className="bg-luxury-purple hover:bg-luxury-purple/90">
              Completar Onboarding
            </Button>
          </Link>
        )}
      </div>
    </div>
  );
};

export default EmptyProfiles;
